import { TBaseLayer, useLayers, useSource } from '@geovelo-frontends/commons';
import { cellToBoundary, polygonToCellsExperimental } from 'h3-js';
import { Map } from 'maplibre-gl';
import { useEffect, useState } from 'react';

export type TH3Cells = Array<{ index: string; isInBiggerRing: boolean }>;

const h3SourceId = 'h3-cells';
export const h3CellsLayerId = 'h3-cells';
const sourceId = 'simplified-traces';
export const simplifiedTracesLayerId = 'simplified-traces';

function useHeatmap({
  displayH3,
  displayTraces,
  map,
  mapInitialized,
  baseLayer,
  h3Cells,
  traces,
}: {
  baseLayer: TBaseLayer;
  displayH3: boolean;
  displayTraces: boolean;
  h3Cells: TH3Cells | undefined;
  map: Map | undefined;
  mapInitialized: boolean;
  traces?: GeoJSON.FeatureCollection<GeoJSON.LineString>;
}) {
  const [sourcesInitialized, setSourcesInitialized] = useState(false);
  const {
    addGeoJSONSource: addH3Source,
    updateGeoJSONSource: updateH3Source,
    clearGeoJSONSource: clearH3Source,
  } = useSource(map, h3SourceId);
  const {
    addGeoJSONSource: addTracesSource,
    updateGeoJSONSource: updateTracesSource,
    clearGeoJSONSource: clearTracesSource,
  } = useSource(map, sourceId);
  const { addLineLayer } = useLayers(map);

  useEffect(() => {
    if (mapInitialized) {
      if (displayH3) {
        addH3Source();
        map?.addLayer(
          {
            id: h3CellsLayerId,
            type: 'fill',
            source: h3SourceId,
            layout: { visibility: 'visible' },
            paint: {
              'fill-color': ['get', 'color'],
              'fill-opacity': ['get', 'opacity'],
              'fill-outline-color': ['get', 'borderColor'],
            },
          },
          'labels',
        );
      }

      if (displayTraces) {
        addTracesSource();
        addLineLayer(
          simplifiedTracesLayerId,
          sourceId,
          {
            'line-join': 'round',
            'line-cap': 'round',
            visibility: 'none',
          },
          {
            'line-color': ['get', 'color'],
            'line-opacity': ['get', 'opacity'],
            'line-width': 3,
          },
        );
      }

      setSourcesInitialized(true);
    }
  }, [mapInitialized]);

  useEffect(() => {
    if (sourcesInitialized && displayH3 && h3Cells) {
      map?.on('move', updateH3Layer);
      updateH3Layer();
    }

    return () => {
      clearH3Source();
      if (sourcesInitialized && displayH3) map?.off('move', updateH3Layer);
    };
  }, [baseLayer, sourcesInitialized, h3Cells]);

  useEffect(() => {
    if (sourcesInitialized && displayTraces && traces) {
      updateTracesSource({
        type: 'FeatureCollection',
        features:
          traces.features?.map(({ ...otherProps }) => ({
            ...otherProps,
            properties: {
              ...otherProps.properties,
              color: baseLayer === 'dark' ? 'rgb(142, 234, 194)' : 'rgb(73, 116, 165)',
              opacity: baseLayer === 'dark' ? 0.4 : 0.5,
            },
          })) || [],
      });
    }

    return () => {
      if (sourcesInitialized && displayTraces) clearTracesSource();
    };
  }, [sourcesInitialized, traces, baseLayer]);

  function updateH3Layer() {
    if (!map) return;

    if (map.getZoom() >= 10) {
      const [[west, south], [east, north]] = map.getBounds().toArray();
      const allCells = polygonToCellsExperimental(
        [
          [west, north],
          [east, north],
          [east, south],
          [west, south],
        ],
        7,
        'containmentOverlappingBbox',
        true,
      );

      const h3CellsMap =
        h3Cells?.reduce<{ [key: string]: { isInBiggerRing: boolean } }>(
          (res, { index, isInBiggerRing }) => {
            res[index] = { isInBiggerRing };
            return res;
          },
          {},
        ) || {};

      updateH3Source({
        type: 'FeatureCollection',
        features: allCells.reduce<GeoJSON.Feature[]>((res, index) => {
          const cell = h3CellsMap[index];
          let color: string, borderColor: string;

          if (cell) {
            if (baseLayer === 'dark') {
              if (cell.isInBiggerRing) borderColor = color = 'rgb(234, 142, 182)';
              else borderColor = color = 'rgb(142, 234, 194)';
            } else {
              if (cell.isInBiggerRing) borderColor = color = 'rgb(165, 73, 116)';
              else borderColor = color = 'rgb(73, 116, 165)';
            }
          } else {
            borderColor = '#888';
            color = 'rgba(0, 0, 0, 0)';
          }

          res.push({
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [cellToBoundary(index, true)] },
            properties: {
              borderColor,
              color,
              opacity: baseLayer === 'dark' ? 0.2 : 0.3,
            },
          });

          return res;
        }, []),
      });
    } else {
      updateH3Source({
        type: 'FeatureCollection',
        features:
          h3Cells?.map(({ index, isInBiggerRing }) => {
            const color =
              baseLayer === 'dark'
                ? isInBiggerRing
                  ? 'rgb(234, 142, 182)'
                  : 'rgb(142, 234, 194)'
                : isInBiggerRing
                  ? 'rgb(165, 73, 116)'
                  : 'rgb(73, 116, 165)';

            return {
              type: 'Feature',
              geometry: { type: 'Polygon', coordinates: [cellToBoundary(index, true)] },
              properties: {
                color,
                borderColor: color,
                opacity: baseLayer === 'dark' ? 0.2 : 0.3,
              },
            };
          }) || [],
      });
    }
  }

  return { sourcesInitialized };
}

export default useHeatmap;
